/**
 * Centralized logging utility
 * 
 * Environment-aware logging that prevents sensitive data leakage
 * and reduces console noise in production.
 * 
 * Constitution Section 19.3: Environment Variables & Configuration
 */

type LogLevel = 'silent' | 'error' | 'warn' | 'info' | 'debug';

const LOG_LEVELS: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

/**
 * Get current log level from environment
 */
function getLogLevel(): LogLevel {
  const envLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined;
  
  if (envLevel && LOG_LEVELS[envLevel] !== undefined) {
    return envLevel;
  }
  
  // Default levels by environment
  if (import.meta.env.PROD) {
    return 'error'; // Production: only errors
  }
  
  return 'info'; // Development: info and above
}

const currentLevel = getLogLevel();
const currentLevelValue = LOG_LEVELS[currentLevel];

/**
 * Mask a token for safe logging (first 8 chars + ... + last 4 chars)
 * Use this ONLY when absolutely necessary for debugging.
 */
export function maskToken(token: string | null | undefined): string {
  if (!token || typeof token !== 'string') {
    return 'null';
  }
  
  if (token.length <= 12) {
    return '***';
  }
  
  return `${token.substring(0, 8)}...${token.substring(token.length - 4)}`;
}

/**
 * Check if a log level should be output
 */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] <= currentLevelValue;
}

/**
 * Centralized logger
 */
export const logger = {
  /**
   * Debug logs - only in development with debug level
   */
  debug: (...args: unknown[]): void => {
    if (shouldLog('debug')) {
      console.debug('[DEBUG]', ...args);
    }
  },
  
  /**
   * Info logs - default in development
   */
  info: (...args: unknown[]): void => {
    if (shouldLog('info')) {
      console.info('[INFO]', ...args);
    }
  },
  
  /**
   * Warning logs - always shown (except silent)
   */
  warn: (...args: unknown[]): void => {
    if (shouldLog('warn')) {
      console.warn('[WARN]', ...args);
    }
  },
  
  /**
   * Error logs - always shown (except silent)
   */
  error: (...args: unknown[]): void => {
    if (shouldLog('error')) {
      console.error('[ERROR]', ...args);
    }
  },
  
  /**
   * Get current log level (for debugging)
   */
  getLevel: (): LogLevel => currentLevel,
};
